import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { apiGet, apiPatch, apiPost } from '../../lib/api.js';
import { Card, Field, SaveButton, LoadingPanel, isDirty } from '../settings/_shared.jsx';
import { Plus, X, Ban, Calendar, ExternalLink } from 'lucide-react';

// PR #421 — Agent self-service scheduler config. Reads/writes the current
// agent's own scheduler block via the /me alias (GET /agents/me/scheduler,
// PATCH /agents/me/scheduler). Foundation for the scheduler (#422-#427).

// Curated IANA zones — the common US zones an agent is likely to work in,
// plus UTC. Not exhaustive; the backend validates any submitted value.
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const PRODUCTS = [
  { key: 'final_expense', label: 'Final Expense' },
  { key: 'general_life', label: 'General Life' },
  { key: 'iul', label: 'IUL' },
  { key: 'veterans_life', label: 'Veterans Life' },
  { key: 'mortgage_protection', label: 'Mortgage Protection' },
];

// pausedUntil is stored as an ISO datetime; the date picker works in YYYY-MM-DD.
function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

function normalizeBusinessHours(bh) {
  const out = {};
  for (const { key } of DAYS) {
    const blocks = Array.isArray(bh?.[key]) ? bh[key] : [];
    out[key] = blocks.map((b) => ({ start: b.start || '09:00', end: b.end || '17:00' }));
  }
  return out;
}

// Preview-only slug normalization — mirrors the backend rules (agents.js /
// Agent.js pre-save) so the user sees the link they'll actually get. The
// backend remains the source of truth on save.
function normalizeSlug(input) {
  return String(input || '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Agent-level Google Calendar connect/status/sync. Self-contained: manages its
// own connection state and talks to the verified /api/calendar/* endpoints.
// Every call is best-effort — a calendar API failure shows an inline notice and
// never crashes the surrounding scheduler page.
function CalendarConnectCard() {
  const [conn, setConn] = useState(null); // null while loading
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const r = await apiGet('/calendar/connection');
      setConn({
        connected: !!r?.connected,
        email: r?.email || '',
        name: r?.name || '',
        syncEnabled: r?.syncEnabled !== false,
      });
      setErr('');
    } catch {
      setConn({ connected: false, email: '', name: '', syncEnabled: true });
      setErr("Couldn't reach calendar service");
    }
  }

  useEffect(() => {
    refresh();
    // Re-check after returning from the Google OAuth consent flow (the callback
    // redirects back to the app, refocusing this window).
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  async function connect() {
    setBusy(true);
    try {
      const r = await apiGet('/calendar/oauth/start');
      if (r?.url) { window.location.href = r.url; return; }
      throw new Error('no url');
    } catch {
      setErr("Couldn't reach calendar service");
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    try {
      await apiPost('/calendar/disconnect');
      await refresh();
    } catch {
      setErr("Couldn't reach calendar service");
    } finally {
      setBusy(false);
    }
  }

  async function toggleSync() {
    const next = !conn.syncEnabled;
    setBusy(true);
    try {
      const r = await apiPost('/calendar/sync-toggle', { enabled: next });
      setConn((c) => ({ ...c, syncEnabled: r?.syncEnabled !== false }));
      setErr('');
    } catch {
      setErr("Couldn't reach calendar service");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Google Calendar"
      description="Connect your Google Calendar so booked appointments sync automatically."
    >
      {conn === null ? (
        <div className="text-sm text-white/50">Checking connection…</div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm">
            {conn.connected ? (
              <span className="text-emerald-400">
                Connected as {conn.email || conn.name || 'Google account'}
              </span>
            ) : (
              <span className="text-white/50">Not connected</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {conn.connected ? (
              <>
                <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={disconnect}>
                  Disconnect
                </button>
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={!!conn.syncEnabled} disabled={busy} onChange={toggleSync} />
                  Sync enabled
                </label>
              </>
            ) : (
              <button
                type="button"
                className="btn text-xs inline-flex items-center gap-1"
                disabled={busy}
                onClick={connect}
              >
                <Calendar className="w-3 h-3" /> Connect
              </button>
            )}
          </div>
          {err && <div className="text-xs text-rose-400">{err}</div>}
        </div>
      )}
    </Card>
  );
}

export default function SchedulerSettings() {
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [slugError, setSlugError] = useState(null);
  const [copied, setCopied] = useState(false);

  async function copyLink(url) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy link');
    }
  }

  useEffect(() => {
    apiGet('/agents/me/scheduler')
      .then((r) => {
        if (r?.ok) {
          const snap = {
            timezone: r.scheduler?.timezone || 'America/Denver',
            businessHours: normalizeBusinessHours(r.scheduler?.businessHours),
            activeInRotation: r.scheduler?.activeInRotation !== false,
            pausedUntil: r.scheduler?.pausedUntil || null,
            productsHandled: Array.isArray(r.scheduler?.productsHandled) ? r.scheduler.productsHandled : [],
            bookingSlug: r.scheduler?.bookingSlug || '',
          };
          setLoaded(snap);
          setDraft(snap);
        }
      })
      .catch(() => toast.error('Failed to load scheduler settings'));
  }, []);

  const dirty = useMemo(() => isDirty(loaded, draft), [loaded, draft]);

  function setField(patch) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  function addBlock(day) {
    setDraft((d) => ({
      ...d,
      businessHours: {
        ...d.businessHours,
        [day]: [...(d.businessHours[day] || []), { start: '09:00', end: '17:00' }],
      },
    }));
  }

  function removeBlock(day, idx) {
    setDraft((d) => ({
      ...d,
      businessHours: {
        ...d.businessHours,
        [day]: d.businessHours[day].filter((_, i) => i !== idx),
      },
    }));
  }

  function updateBlock(day, idx, patch) {
    setDraft((d) => ({
      ...d,
      businessHours: {
        ...d.businessHours,
        [day]: d.businessHours[day].map((b, i) => (i === idx ? { ...b, ...patch } : b)),
      },
    }));
  }

  // Clear a day's windows → agent is unavailable that day (computeSlots returns
  // [] for a day with no businessHours). Draft-only; persisted on save().
  function blockAllDay(day) {
    setDraft((d) => ({
      ...d,
      businessHours: { ...d.businessHours, [day]: [] },
    }));
  }

  // Copy Monday's windows into Tue–Fri (draft-only convenience before save).
  function copyMondayToWeekdays() {
    setDraft((d) => {
      const mon = (d.businessHours.mon || []).map((b) => ({ ...b }));
      const businessHours = { ...d.businessHours };
      for (const day of ['tue', 'wed', 'thu', 'fri']) {
        businessHours[day] = mon.map((b) => ({ ...b }));
      }
      return { ...d, businessHours };
    });
  }

  function toggleProduct(key) {
    setDraft((d) => {
      const has = d.productsHandled.includes(key);
      return {
        ...d,
        productsHandled: has
          ? d.productsHandled.filter((p) => p !== key)
          : [...d.productsHandled, key],
      };
    });
  }

  async function save() {
    if (!draft) return;
    const prev = loaded;
    setBusy(true);
    setSlugError(null);
    // Drop empty day arrays so we persist a clean shape.
    const businessHours = {};
    for (const { key } of DAYS) {
      if (draft.businessHours[key]?.length) businessHours[key] = draft.businessHours[key];
    }
    try {
      const r = await apiPatch('/agents/me/scheduler', {
        timezone: draft.timezone,
        businessHours,
        activeInRotation: draft.activeInRotation,
        pausedUntil: draft.pausedUntil,
        productsHandled: draft.productsHandled,
        bookingSlug: draft.bookingSlug || '',
      });
      if (!r?.ok) throw new Error('Save failed');
      const snap = {
        timezone: r.scheduler?.timezone || draft.timezone,
        businessHours: normalizeBusinessHours(r.scheduler?.businessHours),
        activeInRotation: r.scheduler?.activeInRotation !== false,
        pausedUntil: r.scheduler?.pausedUntil || null,
        productsHandled: Array.isArray(r.scheduler?.productsHandled) ? r.scheduler.productsHandled : [],
        bookingSlug: r.scheduler?.bookingSlug || '',
      };
      setLoaded(snap);
      setDraft(snap);
      toast.success('Scheduler settings saved');
    } catch (e) {
      // Keep the user's draft on slug errors so they can correct the input.
      const code = e?.response?.data?.error;
      if (code === 'slug_taken') {
        setSlugError('That link name is already taken, try another');
      } else if (code === 'invalid_slug') {
        setSlugError('Use letters, numbers and hyphens (3–40 chars)');
      } else {
        setLoaded(prev);
        setDraft(prev);
        toast.error(e?.message || 'Failed to save scheduler settings');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!draft) return <LoadingPanel label="Loading scheduler settings…" />;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const previewSlug = normalizeSlug(draft.bookingSlug);
  const bookingUrl = previewSlug ? `${origin}/book/${previewSlug}` : '';

  return (
    <div className="space-y-6 max-w-3xl">
      <Card
        title="Scheduler"
        description="Control when and which products you take in the lead scheduler."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <Field label="Timezone" hint="Business hours are interpreted in this timezone.">
          <select
            className="input"
            value={draft.timezone}
            onChange={(e) => setField({ timezone: e.target.value })}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </Field>

        <Field label="Active in rotation" hint="Turn off to be skipped by the scheduler entirely.">
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input
              type="checkbox"
              checked={draft.activeInRotation}
              onChange={(e) => setField({ activeInRotation: e.target.checked })}
            />
            {draft.activeInRotation ? 'Active' : 'Paused (not in rotation)'}
          </label>
        </Field>

        <Field label="Pause until" hint="Optional. While set to a future date you stay out of rotation.">
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="input"
              value={toDateInput(draft.pausedUntil)}
              onChange={(e) =>
                setField({ pausedUntil: e.target.value ? new Date(e.target.value).toISOString() : null })
              }
            />
            {draft.pausedUntil && (
              <button type="button" className="btn-ghost text-xs" onClick={() => setField({ pausedUntil: null })}>
                Clear
              </button>
            )}
          </div>
        </Field>
      </Card>

      <CalendarConnectCard />

      <Card
        title="Your public booking link"
        description="Share this link so people can book time with you directly."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-white/60 break-all">
            {bookingUrl || `${origin}/book/…`}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={!bookingUrl}
              onClick={() => copyLink(bookingUrl)}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <a
              className={`btn-ghost text-xs ${bookingUrl ? '' : 'pointer-events-none opacity-40'}`}
              href={bookingUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!bookingUrl}
            >
              Open
            </a>
          </div>
        </div>

        <Field
          label="Link name"
          hint="Letters, numbers and hyphens (3–40 characters). Leave empty to disable your public link."
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-white/40 shrink-0">{origin}/book/</span>
            <input
              className="input flex-1"
              value={draft.bookingSlug}
              placeholder="your-name"
              onChange={(e) => { setField({ bookingSlug: e.target.value }); setSlugError(null); }}
            />
          </div>
          {slugError && <div className="text-xs text-rose-400 mt-1">{slugError}</div>}
          {!bookingUrl && !slugError && (
            <div className="text-xs text-white/35 mt-1">Set a link name to enable your public booking link.</div>
          )}
        </Field>
      </Card>

      <Card title="Products handled" description="Which product types you want the scheduler to route to you.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PRODUCTS.map((p) => (
            <label key={p.key} className="flex items-center gap-2 text-sm text-white/80">
              <input
                type="checkbox"
                checked={draft.productsHandled.includes(p.key)}
                onChange={() => toggleProduct(p.key)}
              />
              {p.label}
            </label>
          ))}
        </div>
      </Card>

      <Card
        title="Business hours"
        description="Set one or more availability windows per day. A day with no windows is unavailable."
        actions={
          <button type="button" className="btn-ghost text-xs" onClick={copyMondayToWeekdays}>
            Copy Monday to weekdays
          </button>
        }
      >
        <div className="divide-y divide-white/5">
          {DAYS.map(({ key, label }) => {
            const blocks = draft.businessHours[key] || [];
            return (
              <div key={key} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <div className="w-24 shrink-0 text-sm font-medium text-white/70 pt-2">{label}</div>
                <div className="flex-1 space-y-2">
                  {blocks.length === 0 && (
                    <div className="text-xs text-white/35 pt-2">Unavailable</div>
                  )}
                  {blocks.map((block, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="time"
                        className="input w-32"
                        value={block.start}
                        onChange={(e) => updateBlock(key, idx, { start: e.target.value })}
                      />
                      <span className="text-white/40">–</span>
                      <input
                        type="time"
                        className="input w-32"
                        value={block.end}
                        onChange={(e) => updateBlock(key, idx, { end: e.target.value })}
                      />
                      <button
                        type="button"
                        className="btn-ghost p-1"
                        aria-label={`Remove ${label} block`}
                        onClick={() => removeBlock(key, idx)}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="btn-ghost text-xs inline-flex items-center gap-1"
                      onClick={() => addBlock(key)}
                    >
                      <Plus className="w-3 h-3" /> Add hours
                    </button>
                    {blocks.length > 0 && (
                      <button
                        type="button"
                        className="btn-ghost text-xs inline-flex items-center gap-1 text-white/50"
                        onClick={() => blockAllDay(key)}
                      >
                        <Ban className="w-3 h-3" /> Block all day
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Bottom actions: no scrolling back to the top to preview or save. */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <a
          href={bookingUrl || undefined}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!bookingUrl}
          data-testid="preview-booking-page-bottom"
          className={`btn-ghost text-sm inline-flex items-center gap-1.5 ${bookingUrl ? '' : 'opacity-40 pointer-events-none'}`}
        >
          <ExternalLink className="w-4 h-4" /> Preview Your Booking Page
        </a>
        <SaveButton onClick={save} dirty={dirty} busy={busy} />
      </div>
    </div>
  );
}
